import { Box } from '@material-ui/core'
import { useState } from 'react'
import { Button } from 'shared/components/Button/Button'
import { Text } from 'shared/components/Text/Text'
import { Handler, HandlerOf } from 'shared/helpers/typeHelper'
import { uploadCDLParserParse, User } from 'shared/models/user'
import { sizes } from 'shared/theme'
import { breakpoints } from 'shared/theme/breakpoints'
import { midGray } from 'shared/theme/colors'
import { styled } from 'styled-components'

import { useUserProvider } from '../../../Context/UserContext'
import { UploadCDL } from '../UploadCDL'
import { CDLForm } from './CDLInformationParser.constants'

interface CDLInformationUploadProps {
  nextStep: Handler
  setParsedCDL: HandlerOf<CDLForm | null>
  user: User
}

const CDL_UPLOAD_ERROR_MESSAGE =
  'Please upload a file with the supported format — Max. 30MB,  jpg, png, gif, pdf.'

export const CDLInformationUpload = ({ nextStep, setParsedCDL, user }: CDLInformationUploadProps) => {
  const [uploadedFront, setUploadedFront] = useState<File | undefined>(undefined)
  const [uploadedFrontError, setUploadedFrontError] = useState<string | undefined>(undefined)
  const [uploadedBack, setUploadedBack] = useState<File | undefined>(undefined)
  const [uploadedBackError, setUploadedBackError] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const { showBanner, clearUserCache } = useUserProvider()

  const handleSetUploadedFront = (file: File | undefined) => {
    setUploadedFront(file)
    setUploadedFrontError(undefined)
    setUploadedBackError(undefined)
  }

  const handleSetUploadedBack = (file: File | undefined) => {
    setUploadedBack(file)
    setUploadedFrontError(undefined)
    setUploadedBackError(undefined)
  }

  const uploadCDLImages = async () => {
    const formData = new FormData()
    if (uploadedFront) {
      formData.append('license_front', uploadedFront)
    }
    if (uploadedBack) {
      formData.append('license_back', uploadedBack)
    }

    try {
      const res = await uploadCDLParserParse(user.id, formData)
      setParsedCDL(res)
      return res
    } catch (err: any) {
      onShowErrorBanner(!!uploadedFrontError, !!uploadedBackError)
      setLoading(false)
      return null
    }
  }

  const onSubmit = async () => {
    // If there are previously uploaded images and the form is unchanged, skip the upload, reset parsed data, go to next step
    if (
      !uploadedFront &&
      !uploadedFrontError &&
      user.driver?.licenseFront &&
      !uploadedBack &&
      !uploadedBackError &&
      user.driver?.licenseBack
    ) {
      setParsedCDL(null)
      return nextStep()
    }

    const uploadedFrontSuccess = !!uploadedFront && !uploadedFrontError
    const uploadedBackSuccess = !!uploadedBack && !uploadedBackError

    if (!uploadedFrontSuccess) {
      setUploadedFrontError(CDL_UPLOAD_ERROR_MESSAGE)
    }

    if (!uploadedBackSuccess) {
      setUploadedBackError(CDL_UPLOAD_ERROR_MESSAGE)
    }

    if (!uploadedFrontSuccess || !uploadedBackSuccess) {
      return
    }

    try {
      setLoading(true)
      await uploadCDLImages()
      await clearUserCache()
      setLoading(false)
      nextStep()
    } catch {
      setLoading(false)
    }
  }

  const onShowErrorBanner = (frontError: boolean, backError: boolean) => {
    if (frontError || backError) {
      showBanner('error', {
        message: `Couldn't upload ${
          frontError && backError ? 'neither front or back' : frontError ? 'front' : 'back'
        } side of driver's license. Please try uploading it again.`,
      })
    }
  }

  return (
    <Container>
      <Title variant="header2" align="center" mb={1}>
        Upload your commercial driver’s license
      </Title>
      <Text variant="paragraph2" align="center" color={midGray}>
        Make sure the images are well lit and the text is readable.
      </Text>
      <Box mt={5} />

      <UploadCDL
        handleFrontFileChange={handleSetUploadedFront}
        handleBackFileChange={handleSetUploadedBack}
        uploadedFrontFile={uploadedBack ? undefined : user.driver?.licenseFront}
        uploadedBackFile={uploadedFront ? undefined : user.driver?.licenseBack}
        frontErrorMessage={uploadedFrontError}
        backErrorMessage={uploadedBackError}
      />
      <Button type="submit" size={sizes.extraLarge} isLoading={loading} margin="0 auto" onClick={onSubmit}>
        Continue
      </Button>
    </Container>
  )
}

const Container = styled.div`
  position: relative;
  margin: 0 auto;
  max-width: 100%;
  width: 100%;
`

const Title = styled(Text)`
  margin-left: -5%;
  margin-right: -5%;

  @media ${breakpoints.sm} {
    margin-left: unset;
    margin-right: unset;
  }
`
